/**
 * RecentVendorsStrip — Iter 67ad.
 *
 * Slim horizontal strip showing the last (up to 5) hosts the renter clicked
 * through to. Self-hides when the list is empty. Each tile is a click-through
 * to that host's full vehicle inventory (`/cars?vendor_id=…`), giving repeat
 * renters a one-click path back to a host they liked.
 *
 * Listens to the same-tab `rentalcar:recent-vendors-updated` event so the
 * strip refreshes instantly when a new host is added (e.g. user clicks a
 * carousel tile) without any prop wiring.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock3, X } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { getRecentVendors, clearRecentVendors } from "@/lib/recentVendors";

export default function RecentVendorsStrip({ className = "" }) {
  const [recent, setRecent] = useState(() => getRecentVendors());

  useEffect(() => {
    const refresh = () => setRecent(getRecentVendors());
    // Cross-tab updates fire `storage`; same-tab updates fire our custom event.
    window.addEventListener("storage", refresh);
    window.addEventListener("rentalcar:recent-vendors-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("rentalcar:recent-vendors-updated", refresh);
    };
  }, []);

  if (!recent.length) return null;

  return (
    <section
      className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}
      data-testid="recent-vendors-strip"
    >
      <div className="rounded-3xl border border-border bg-white/70 backdrop-blur px-4 sm:px-5 py-3.5 flex items-center gap-3 sm:gap-5 shadow-sm">
        <div className="flex items-center gap-2 shrink-0">
          <div className="h-7 w-7 rounded-full bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center">
            <Clock3 className="h-3.5 w-3.5 text-amber-600" />
          </div>
          <div className="hidden sm:block">
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500">Recently viewed</div>
            <div className="text-xs text-neutral-700 font-medium">Pick up where you left off</div>
          </div>
        </div>

        <div className="flex-1 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="recent-vendors-list">
          {recent.map((v) => (
            <RecentVendorTile key={v.id} vendor={v} />
          ))}
        </div>

        <button
          type="button"
          onClick={() => clearRecentVendors()}
          className="shrink-0 h-7 w-7 rounded-full border border-border text-neutral-400 hover:text-neutral-700 hover:border-neutral-300 flex items-center justify-center transition"
          title="Clear recently viewed hosts"
          data-testid="recent-vendors-clear"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  );
}

function RecentVendorTile({ vendor }) {
  const [broken, setBroken] = useState(false);
  const src = vendor.logo ? fileUrl(vendor.logo) : null;
  const initial = (vendor.name || "?").trim()[0]?.toUpperCase() || "?";

  return (
    <Link
      to={`/cars?vendor_id=${encodeURIComponent(vendor.id)}`}
      className="group/recent shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-white hover:border-sky-300 hover:shadow-sm transition"
      title={`See ${vendor.name}'s vehicles`}
      data-testid={`recent-vendor-tile-${vendor.id}`}
    >
      {!broken && src ? (
        <img
          src={src}
          alt={vendor.name}
          className="h-6 w-6 rounded-full object-contain bg-neutral-50 p-0.5 ring-1 ring-neutral-200"
          onError={() => setBroken(true)}
          loading="lazy"
        />
      ) : (
        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-sky-100 to-indigo-100 flex items-center justify-center text-[10px] font-semibold text-indigo-700 ring-1 ring-indigo-100">
          {initial}
        </div>
      )}
      <span className="text-xs font-medium text-neutral-800 truncate max-w-[140px] group-hover/recent:text-sky-700">{vendor.name}</span>
    </Link>
  );
}
